import { useState } from "react";
import { Building2, Clock, LogOut, Users } from "lucide-react";
import { api, useSubmit } from "@/lib/api";
import { Brand, ErrorBox, Field } from "@/components/common";

export const INTENT_KEY = "df_signup_intent";
export const JOIN_CODE_KEY = "df_join_code";
const ROLES = [{ value: "dentist", label: "Dentist" }, { value: "receptionist", label: "Receptionist" }, { value: "accountant", label: "Accountant" }, { value: "admin", label: "Clinic administrator" }];

function NewClinic({ member, onDone }) {
  const [form, setForm] = useState({ clinic_name: "", your_name: member.name || "" });
  const { busy, error, run } = useSubmit();
  const submit = (e) => { e.preventDefault(); run(async () => { await api.post("/onboarding/clinic", form); localStorage.removeItem(INTENT_KEY); onDone(); }); };
  return (
    <form className="onboard-form" onSubmit={submit} data-testid="register-clinic-form">
      <Field label="Clinic name" required testid="onboard-clinic-name-input" value={form.clinic_name} onChange={(v) => setForm({ ...form, clinic_name: v })} placeholder="e.g. Smile Care Dental Clinic" />
      <Field label="Your name" testid="onboard-clinic-your-name-input" value={form.your_name} onChange={(v) => setForm({ ...form, your_name: v })} />
      <p className="muted small-note">You become this clinic's administrator. Your team joins with a clinic code you share, and you approve each person.</p>
      <ErrorBox error={error} testid="register-clinic-error" />
      <button className="primary full" disabled={busy} data-testid="register-clinic-button"><Building2 size={16} /> {busy ? "Creating…" : "Create my clinic"}</button>
    </form>
  );
}

function JoinClinic({ member, onDone }) {
  const [form, setForm] = useState({ join_code: localStorage.getItem(JOIN_CODE_KEY) || "", role: "dentist", your_name: member.name || "" });
  const { busy, error, run } = useSubmit();
  const submit = (e) => { e.preventDefault(); run(async () => { await api.post("/onboarding/join", form); localStorage.removeItem(INTENT_KEY); localStorage.removeItem(JOIN_CODE_KEY); onDone(); }); };
  return (
    <form className="onboard-form" onSubmit={submit} data-testid="join-clinic-form">
      <Field label="Clinic code (ask your clinic administrator)" required testid="onboard-join-code-input" value={form.join_code} onChange={(v) => setForm({ ...form, join_code: v.toUpperCase() })} placeholder="e.g. K7M2QXP" />
      <Field label="I am joining as" testid="onboard-role-select" value={form.role} onChange={(v) => setForm({ ...form, role: v })} options={ROLES} />
      <Field label="Your name" testid="onboard-join-your-name-input" value={form.your_name} onChange={(v) => setForm({ ...form, your_name: v })} />
      <ErrorBox error={error} testid="join-clinic-error" />
      <button className="primary full" disabled={busy} data-testid="join-clinic-button"><Users size={16} /> {busy ? "Sending…" : "Send request to the clinic admin"}</button>
    </form>
  );
}

export function Onboarding({ member, onDone, onSignOut }) {
  const [mode, setMode] = useState(localStorage.getItem(INTENT_KEY) === "join" ? "join" : "clinic");
  return (
    <main className="onboard-shell" data-testid="onboarding">
      <div className="onboard-card">
        <Brand />
        <p className="eyebrow">WELCOME{member.email ? ` · ${member.email.toUpperCase()}` : ""}</p>
        <h2>How will you use DentaflowAI?</h2>
        <div className="choice-row">
          <button type="button" className={mode === "clinic" ? "choice active" : "choice"} onClick={() => setMode("clinic")} data-testid="onboard-choice-clinic"><Building2 size={18} /><b>Register a new clinic</b><small>I own or run the clinic</small></button>
          <button type="button" className={mode === "join" ? "choice active" : "choice"} onClick={() => setMode("join")} data-testid="onboard-choice-join"><Users size={18} /><b>Join my clinic's team</b><small>Dentist, receptionist, accountant or admin</small></button>
        </div>
        {mode === "clinic" ? <NewClinic member={member} onDone={onDone} /> : <JoinClinic member={member} onDone={onDone} />}
        <button className="logout" onClick={onSignOut} data-testid="onboard-sign-out-button"><LogOut size={14} /> Sign out</button>
      </div>
    </main>
  );
}

export function PendingAccess({ member, onDone, onSignOut }) {
  const { busy, run } = useSubmit();
  const label = ROLES.find((r) => r.value === member.requested_role)?.label || "team member";
  return (
    <main className="onboard-shell">
      <div className="onboard-card" data-testid="pending-access">
        <Brand />
        <div className="pending-icon"><Clock size={26} /></div>
        <p className="eyebrow">WAITING FOR APPROVAL</p>
        <h2>Request sent to {member.clinic?.name || "your clinic"}.</h2>
        <p className="muted">You asked to join as <b>{label}</b>. The clinic administrator will approve you from Team &amp; Access. You will see the clinic workspace as soon as they do.</p>
        <button className="primary full" onClick={onDone} data-testid="pending-refresh-button">Check again</button>
        <button className="secondary full" disabled={busy} onClick={() => run(async () => { await api.post("/onboarding/cancel"); onDone(); })} data-testid="pending-cancel-button">Cancel request / choose another clinic</button>
        <button className="logout" onClick={onSignOut} data-testid="pending-sign-out-button"><LogOut size={14} /> Sign out</button>
      </div>
    </main>
  );
}
